import PropTypes from 'prop-types'

import CATEGORIES_QUERY from 'graphql/categories.query'
import POSTS_QUERY from 'graphql/posts.query'
import getSitesForStaticPaths from 'helpers/getSitesForStaticPaths'
import uppercaseFirst from 'helpers/uppercaseFirst'

import Layout from 'components/Layout'
import WordpressSEO from 'components/WordpressSEO'
import NewsHeader from 'components/NewsHeader'
import Pagination from 'components/Pagination'
import StateInitializer from '../../../../components/StateInitializer'
import getGlobalSettings from '../../../../helpers/getGlobalSettings'
import getSites from '../../../../helpers/getSites'
import ArticleGrid from '../../../../components/ArticleGrid'
import { createApolloClient } from '../../../../lib/createApolloClient'
import * as Sentry from '@sentry/nextjs'

function Category({
  globalSettings,
  sites,
  siteSlug,
  offsetPagination = {},
  categorySlug = '',
  site = '',
  posts = [],
  categoriesMenu = [],
}) {
  return (
    <StateInitializer
      globalSettings={globalSettings}
      sites={sites}
      siteSlug={siteSlug}
    >
      <Layout globalSettings={globalSettings}>
        <WordpressSEO
          url={`/${site}/news/${categorySlug}`}
          title={uppercaseFirst(categorySlug)}
          desc="Latest news from our talent and models at Select Model London. Select Model Management is an iconic global network of boutique model and talent agencies."
          image={posts?.[0]?.acfPost?.featuredImage?.node?.mediaItemUrl}
        />
        <NewsHeader categorySlug={categorySlug} categories={categoriesMenu} />
        <ArticleGrid articles={posts} />
        <Pagination pagination={offsetPagination} currentPage={1} />
      </Layout>
    </StateInitializer>
  )
}

Category.propTypes = {
  globalSettings: PropTypes.object.isRequired,
  sites: PropTypes.array.isRequired,
  siteSlug: PropTypes.string.isRequired,
}

export default Category

export async function getStaticProps({ params }) {
  const { site, categorySlug } = params

  Sentry.addBreadcrumb({
    message: `Loading News Category props - site: ${site}, categorySlug: ${categorySlug}`,
    category: 'getStaticProps',
    level: 'info',
    data: { params },
  })

  const apolloClient = createApolloClient()

  const [globalSettings, sites, { data, error }] = await Promise.all([
    getGlobalSettings(site),
    getSites(),
    apolloClient.query({
      query: POSTS_QUERY,
      context: { site },
      variables: {
        categoryName: categorySlug,
        withCategoriesMenu: true,
        featuredInBlocks: true,
      },
    }),
  ])

  if (error) {
    Sentry.captureException(
      new Error(`Error loading News Category props - ${error}`)
    )
    console.error(`Error loading News Category props - ${error}`)
  }

  return {
    props: {
      ...params,
      posts: data.posts.nodes,
      categoriesMenu:
        data.newsPage?.nodes?.[0]?.acfNews?.categoriesMenu?.nodes ?? null,
      offsetPagination: data.posts.pageInfo.offsetPagination,
      globalSettings,
      sites,
      siteSlug: site,
    },
    revalidate: 60 * 60,
  }
}

export async function getStaticPaths() {
  const sites = await getSitesForStaticPaths(
    ({ site }) => ({
      query: CATEGORIES_QUERY,
      context: { site },
    }),
    (data) =>
      data.categories.nodes
        .filter((c) => c.slug !== 'uncategorised')
        .map((c) => c.slug)
  )
  const paths = []
  sites.forEach((site) => {
    site[1].forEach((p) =>
      paths.push({
        params: {
          site: site[0],
          categorySlug: p,
        },
      })
    )
  })

  return {
    paths,
    fallback: false,
  }
}
